import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Heading } from "../../../../components/heading/Heading";
import { useDagCommunityViewQuery, useViewerQuery } from "../../../../graphql/schema";
import { Section } from "../../../../components/section/Section";
import { Loader } from "../../../../components/loader/Loader";
import { DagCommunityOverview } from "./DagCommunityOverview";
import { DagCommunityDagsToScr } from "./DagCommunityDagsToScr";
import { DagCommunityDeposit } from "./DagCommunityDeposit";
import { DagCommunityDagsToBroker } from "./DagCommunityDagtoBroker";
import { DagCommunityMarket } from "./DagCommunityMarket";
import Tabs from "../../../../components/tabs/Tabs";
import { formatNumberWithSpaces } from "../../../../components/price/Price";

import styles from "./dag-community-view.module.scss";

export interface DagCommunityViewProps {
  className?: string;
}

export const DagCommunityView: FC<DagCommunityViewProps> = () => {
  // access translation keys
  const { t } = useTranslation();

  const { data: me, refetch: meRefetch, loading: meLoading } = useViewerQuery({ fetchPolicy: "network-only" });
  const { data: totalDagsQuery } = useDagCommunityViewQuery({ fetchPolicy: "network-only" });
  const [currentRate, setCurrentRate] = useState("0.002");

  useEffect(() => {
    if (totalDagsQuery?.getTotalBurned?.rate) {
      if (totalDagsQuery?.getTotalBurned?.rate !== 500) {
        setCurrentRate("0.006");
      }
    }
  }, [totalDagsQuery?.getTotalBurned?.rate]);

  const tabs = [
    {
      id: "overview",
      label: "Overview",
      content: () => <DagCommunityOverview me={me} totalDagsQuery={totalDagsQuery} />,
    },
    {
      id: "deposit",
      label: "Deposit/Withdraw",
      content: () => <DagCommunityDeposit me={me} totalDagsQuery={totalDagsQuery} meRefetch={meRefetch} />,
    },
    {
      id: "scr",
      label: "DAGS to SCR",
      content: () => <DagCommunityDagsToScr me={me} totalDagsQuery={totalDagsQuery} meRefetch={meRefetch} />,
    },
    {
      id: "broker",
      label: "DAGS to Broker",
      content: () => (
        <DagCommunityDagsToBroker
          me={me}
          totalDagsQuery={totalDagsQuery}
          currentRate={currentRate}
          meRefetch={meRefetch}
        />
      ),
    },
    {
      id: "market",
      label: "DAGS to Market",
      content: () => <DagCommunityMarket />,
    },
    {
      id: "liquidity",
      label: "DAGS to Liquidity",
      content: <p className="coming-soon">Coming soon...</p>,
    },
    {
      id: "travel",
      label: "DAGS to Travel",
      content: <p className="coming-soon">Coming soon...</p>,
    },
  ];

  return (
    <Section gutter={8} withSpace className={styles.wrap}>
      <Loader cover visibleOnFirstLoad={{ loading: meLoading }} />
      <div className={styles["header-row"]}>
        <Heading className={styles.mainTitle} gutter={8} level={4}>
          {t("DAGS Community")}
        </Heading>

        <div className={styles["info-row"]}>
          <p className={styles.balance}>
            <strong>{t("Your DAGS Balance")}:</strong> {formatNumberWithSpaces(me?.viewer?.coinAccountBalance ?? 0)}{" "}
            DAGS
          </p>

          <div className={styles["separator-title"]} />

          <p className={styles.burned}>
            <strong>{t("Total DAGS burned by community")}:</strong>{" "}
            {formatNumberWithSpaces(parseFloat(totalDagsQuery?.getTotalBurned?.totalDags ?? "0"))} DAGS
          </p>
        </div>
      </div>
      <div className={styles["main-content"]}>
        <Tabs tabs={tabs} defaultTab="overview" />
      </div>
    </Section>
  );
};
